import React, {useState} from 'react';
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Flex,
  Text,
  HStack,
  Button,
  Tooltip,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  SimpleGrid,
  Stack, CircularProgress
} from "@chakra-ui/react";
import {EmailLogEntry} from "@app/core-app/app/data/email-log-entry";
import {formatDistanceToNow} from "date-fns";
import {CheckCircleIcon, InfoOutlineIcon, RepeatIcon, ViewIcon} from "@chakra-ui/icons";
import {IoMail, IoStatsChart, IoWarning} from 'react-icons/io5';
import {IoIosMail} from "react-icons/io";
import {ArrowLeftIcon} from "@heroicons/react/outline";
import {useOutboxLogDetailedStats} from "../../../data/outbox-log";
import {SESEvent} from "@app/core-app/app/data/ses/ses-event";
import {event} from "../../../services/analytics";

type EmailStatsTableProps = {
  stats: EmailLogEntry[] | undefined | null;
  loading: boolean;
};

const OutboxStatsTable: React.FC<EmailStatsTableProps & {
  setSelectedDetails: (detail: EmailLogEntry | null) => void,
  selectedDetails: EmailLogEntry | null
}> = ({stats, loading, setSelectedDetails, selectedDetails}) => {
  const [displayCount, setDisplayCount] = useState(15);

  const handleShowMore = () => {
    setDisplayCount(prevCount => prevCount + 15);
  };

  return (
    <Box as={TableContainer}>
      <Table variant="simple" colorScheme="gray" size={"sm"} w={"full"}>
        <Thead>
          <Tr>
            <Th>Page Title</Th>
            <Th></Th>
            <Th isNumeric>Recipients</Th>
            <Th isNumeric>Emails Sent</Th>
            <Th isNumeric>Delivered</Th>
            <Th isNumeric>Opened</Th>
            <Th isNumeric>Clicks</Th>
            <Th isNumeric>Bounce</Th>
            <Th isNumeric>Sent At</Th>
            <Th isNumeric>Errors</Th>
          </Tr>
        </Thead>
        <Tbody>
          {stats?.slice(0, displayCount)?.map((row, index) => (
            <Tr key={index}>
              <Td><HStack alignItems={"center"} spacing={1}><IoIosMail/><Text>{row.pageTitle}</Text></HStack></Td>
              <Td><Button size={"xs"} variant={"outline"} onClick={() => setSelectedDetails(row)}>Open</Button></Td>
              <Td isNumeric>
                <Tooltip label={row.to.join(", ")} fontSize={"xs"}>
                  <HStack spacing={1}>
                    <span>{row.to.length}</span>
                    <InfoOutlineIcon fontSize={"xs"}/>
                  </HStack>
                </Tooltip>
              </Td>
              <Td isNumeric>{row.stats["Send"]}</Td>
              <Td isNumeric>{row.stats["Delivery"]}</Td>
              <Td isNumeric>{row.stats["Open"]}</Td>
              <Td isNumeric>{row.stats["Click"]}</Td>
              <Td isNumeric>{row.stats["Bounce"]}</Td>
              <Td isNumeric>{formatDistanceToNow(new Date(row.timestamp), {addSuffix: true})}</Td>
              <Td isNumeric>{row.stats["OutboxFailure"]}</Td>
            </Tr>
          ))}
          {!stats?.length && <Tr><Td colSpan={10}>No data</Td></Tr>}
          {loading && <Tr><Td colSpan={10}>Loading...</Td></Tr>}
        </Tbody>
      </Table>
      {stats && displayCount < stats.length && (
        <Button size="sm" colorScheme={"blue"} variant={"outline"} onClick={handleShowMore} mt={4}>
          Show More
        </Button>
      )}
    </Box>
  );
};

const DetailsView: React.FC<{ selectedDetails: EmailLogEntry, goBack: () => void }> = ({selectedDetails, goBack}) => {
  const {pageTitle, to, timestamp, stats, _id} = selectedDetails;

  const uniqueDestinations =[...new Set(to.map(x=> x.trim()))]
  // const uniqueDestinations = to

  const {data: detailedLogData, isLoading} = useOutboxLogDetailedStats(_id.toString())
  const sendDate = formatDistanceToNow(new Date(timestamp), {addSuffix: true});
  const errorEvents: SESEvent[] = detailedLogData?.outboxFailures


  const sections = [
    {
      title: "Base Stats",
      cards: [
        {title: "Sent", value: stats["Send"], icon: <IoMail/>},
        {title: "Delivered", value: stats["Delivery"], icon: <CheckCircleIcon/>},
        {title: "Errors", value: stats["OutboxFailure"], icon: <IoWarning/>},
      ],
    },
    {
      title: "Actions",
      cards: [
        {title: "Opened", value: stats["Open"], icon: <ViewIcon/>},
        {title: "Clicks", value: stats["Click"], icon: <IoStatsChart/>},
      ],
    },
    {
      title: "Reputation",
      cards: [
        {title: "Bounce", value: stats["Bounce"], icon: <RepeatIcon/>},
        {title: "Complaint", value: stats["Complaint"], icon: <IoWarning/>},
      ],
    },
  ];

  return (
    <Box pb={4}>
      <Flex alignItems={"center"}>
        <Text fontSize="xl" fontWeight="bold" mb={0}>{pageTitle}</Text>
        {isLoading && <CircularProgress size={5} ml={2} isIndeterminate color='blue.500'/>}
      </Flex>

      <Box mb={4}>
        <Text color="gray.500">Sent {sendDate}</Text>
      </Box>
      {sections.map(({title, cards}, index) => (
        <Box key={index} my={4}>
          <Text my={2} px={0} color={"gray.500"} fontWeight={"medium"}>{title}</Text>
          <Stack direction={{base: "column", md: "row"}} gap={4}>
            {cards.map((card, index) => (
              <Flex key={index} flexDirection={"column"} border={"1px solid"} borderColor={'gray.200'}
                borderRadius={"md"} px={4} py={4} w={200}>
                <Flex alignItems={"center"}>
                  <Flex h={6} w={4} alignItems={"center"} justifyContent={"center"}>{card.icon}</Flex>
                  <Text ml={2}>{card.title}</Text>
                </Flex>
                <Text fontWeight="bold">{card.value || 0}</Text>
              </Flex>
            ))}
          </Stack>
        </Box>
      ))}

      <SimpleGrid columns={{base: 1, md: 1}} gap={4}>
        <Box>
          <Text mb={2} color={"gray.500"} fontWeight={"medium"}>Email Recipients</Text>
          <Box py={2} px={0} border="1px" borderColor="gray.200" borderRadius="md" maxH="350px" overflowY="auto">
            <TableContainer>
              <Table variant="simple" size="sm">
                <Thead>
                  <Tr>
                    <Th>No.</Th>
                    <Th>Email</Th>
                    <Th isNumeric>Emails Sent</Th>
                    <Th isNumeric>Delivered</Th>
                    <Th isNumeric>Opened</Th>
                    <Th isNumeric>Clicks</Th>
                    <Th isNumeric>Bounce</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {uniqueDestinations.map((email, index) => {

                    const destinationStatsData = detailedLogData?.destinationStats[email?.trim()] || {}
                    return <Tr key={index}>
                      <Td>{index + 1}</Td>
                      <Td>{email}</Td>
                      <Td isNumeric>{destinationStatsData["Send"]}</Td>
                      <Td isNumeric>{destinationStatsData["Delivery"]}</Td>
                      <Td isNumeric>{destinationStatsData["Open"]}</Td>
                      <Td isNumeric>{destinationStatsData["Click"]}</Td>
                      <Td isNumeric>{destinationStatsData["Bounce"]}</Td>
                    </Tr>}
                  )}
                </Tbody>
              </Table>
            </TableContainer>
          </Box>
        </Box>
        <Box>
          <Text mb={2} color={"gray.500"} fontWeight={"medium"}>Processing Errors</Text>
          <Box p={3} border="1px" borderColor="gray.200" borderRadius="md">
            {!errorEvents?.length && !isLoading && <Text color={"gray.500"} fontSize={"sm"}>No errors</Text>}
            {errorEvents && <Box>
              {errorEvents.map((event, index) => {
                let message = event.errorMessage
                if(!message){
                  message = event.raw?.error?.message;
                }
                return <Box key={index} py={0.5}>
                  <Text fontSize={"sm"}>{message || 'Unknown error'}</Text>
                </Box>
              })
              }


            </Box>}

          </Box>
        </Box>
      </SimpleGrid>
    </Box>
  );
};

export const OutboxStatsModal: React.FC<EmailStatsTableProps> = ({stats, loading}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedDetails, setSelectedDetails] = useState<EmailLogEntry | null>(null);

  const onClose = () => {
    if (selectedDetails) {
      setSelectedDetails(null);
    } else {
      setIsOpen(false);
    }
  };

  const openDetails = (detail: EmailLogEntry) => {
    event({action: "open_outbox_detailed_stats_modal", params: {logId: detail?._id?.toString()}})
    setSelectedDetails(detail);
  };

  const goBack = () => {
    setSelectedDetails(null);
  };


  const onOpen = () => {
    event({action: "open_outbox_stats_modal", params: {}})
    setIsOpen(true);
  }
  return (
    <>
      <Button onClick={() => onOpen()} colorScheme={"blue"} size={"sm"} variant={"outline"}>
        View Sending Statistics
      </Button>
      <Modal isOpen={isOpen} onClose={onClose} size={"6xl"}>
        <ModalOverlay/>
        <ModalContent>
          <ModalHeader>
            <Flex alignItems={"center"}>
              {selectedDetails && <Box w={6} h={6} onClick={onClose} cursor={"pointer"}>
                <ArrowLeftIcon/>
              </Box>
              }

              <Text ml={2}>
                {selectedDetails ? `Details for ${selectedDetails.pageTitle}` : 'Sending Statistics'}
              </Text>
            </Flex>

          </ModalHeader>
          <ModalCloseButton/>
          <ModalBody>
            {selectedDetails ?
              <DetailsView selectedDetails={selectedDetails} goBack={goBack}/>
              :
              <OutboxStatsTable stats={stats} loading={loading} setSelectedDetails={openDetails}
                selectedDetails={selectedDetails}/>
            }
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};

export default OutboxStatsModal;


